import wafReport from '@/utils/axios/waf-report'
import datacentral from '@/utils/axios/datacentral'

export const convertToWebp = async ({
    files,
    quality = 80,
}: {
    files: File[]
    quality?: number
}): Promise<Blob> => {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))

    const response = await datacentral.post(
        `/uploads/convert-webp?quality=${quality}`,
        formData,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
            responseType: 'blob',
        }
    )
    return response.data
}

/* ── WAF FREE report ──────────────────────────────────────────────────────
 * Gọi thẳng servicemgmt (@mgmt), KHÔNG qua dc-be — báo cáo này không có
 * endpoint nào bên dc-be. `servicemgmt` đã gắn sẵn RPT qua authSdk, nên
 * claim `groups: ["/Admin"]` mà EmployeeRequired đòi hỏi được kèm tự động.
 */

/** Một dòng dịch vụ WAAP trong báo cáo. */
export interface WaapServiceRow {
    id: string
    service_name: string
    /** = customers.customer_id bên DC (dạng chuỗi) */
    organization_id: string
    status: 'active' | 'suspended' | 'terminated' | 'archived'
    /** Ngày kích hoạt WAF */
    created_at: string
    provider?: {
        id: string
        name: string
        display_name: string
        type: string
        legacy_scope: string
        category: string
    }
    entity_uids: string[]
    /** 0 = CHƯA trỏ domain. Đây là cột nghiệp vụ chính. */
    domain_count: number
    /** VẮNG MẶT khi domain_count = 0. Tối đa 20 phần tử. */
    domains?: Array<{ name: string; binding_status: string }>
    waap_package_id?: string
    waap_package_name?: string
    /** null khi không tra được khách bên DC */
    customer?: {
        customer_id: number
        customer_name: string
        customer_company_name?: string
        customer_email?: string
        /** null (không phải mảng rỗng) khi khách chưa có ai theo dõi */
        sale_followers?: Array<{
            employee_id: number
            employee_name: string
        }> | null
    } | null
}

export interface WaapReportPagination {
    total: number
    page: number
    page_size: number
    total_pages: number
    page_next: number
    page_prev: number
}

export interface Paginated<T> {
    message: string
    results: T[]
    pagination: WaapReportPagination
}

export interface WafFreeReportParams {
    /** `free` = gói 4001, `paid` = 4002/4003/4004 */
    package?: 'free' | 'paid' | ''
    status?: WaapServiceRow['status'] | ''
    /** RFC3339. Sai định dạng sẽ bị BE bỏ qua (nới rộng khoảng), không báo lỗi. */
    from?: string
    to?: string
    q?: string
    page?: number
    per_page?: number
}

/** BE ép per_page về 100 nếu truyền lớn hơn. */
export const WAF_REPORT_MAX_PER_PAGE = 100

const buildParams = (params: WafFreeReportParams) => {
    const out: Record<string, string | number> = { category: 'waap' }
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && value !== null) {
            out[key] = value as string | number
        }
    })
    return out
}

export const getWafFreeReport = async (
    params: WafFreeReportParams = {}
): Promise<Paginated<WaapServiceRow>> => {
    const response = await wafReport.get<Paginated<WaapServiceRow>>(
        '/internal/ops/services',
        { params: buildParams(params) }
    )
    return response.data
}

/**
 * Lấy TOÀN BỘ trang cho export. Endpoint cap 100/trang nên phải lặp.
 * Mỗi trang BE gọi DataCentral tuần tự 1 request/khách để lấy tên+email,
 * nên tránh gọi hàm này liên tục.
 */
export const getAllWafFreeReport = async (
    params: WafFreeReportParams = {}
): Promise<WaapServiceRow[]> => {
    const rows: WaapServiceRow[] = []
    let page = 1
    let totalPages = 1
    do {
        const data = await getWafFreeReport({
            ...params,
            per_page: WAF_REPORT_MAX_PER_PAGE,
            page,
        })
        rows.push(...data.results)
        totalPages = data.pagination?.total_pages ?? 1
        page += 1
    } while (page <= totalPages)
    return rows
}

/**
 * Gán thêm 1 sale follower cho khách — gọi DC (không phải servicemgmt).
 * Dùng route append riêng thay vì bulk-reassign: bulk-reassign ghi đè cả mảng
 * follower và ép customer_source_type = 'internal', làm khách tự đăng ký bị gắn
 * nhãn thành khách do marketing kéo về.
 */
export const addSaleFollower = async ({
    customerId,
    employeeId,
}: {
    customerId: number
    employeeId: number
}) => {
    const response = await datacentral.post(
        `/customers/${customerId}/sale-followers`,
        { employee_id: employeeId }
    )
    return response.data
}
